import { useState, useRef, useMemo } from "react";
// @ts-ignore
import {
  GoogleMap,
  Marker,
  Autocomplete,
  useJsApiLoader,
  Libraries,
} from "@react-google-maps/api";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import { useTranslation } from "react-i18next";
import { colors } from "../../../../theme/palette";
import {
  LocationIcon,
  LocationInputEndIcon,
  LocationInputPlaceholderIcon,
  LocatorIcon,
} from "../../../../assets/icons/MUI/EventsIcons";

const containerStyle = {
  width: "100%",
  height: "200px",
};

const defaultCenter = {
  lat: 30.0544,
  lng: 31.2071,
};

type Location = {
  formattedAddress: string;
  lat: number;
  lng: number;
};

const MapWithPlacePicker = ({
  onLocationChange,
  locationError,
  initialLocation,
}: {
  onLocationChange: (location: Location | null, error?: string) => void;
  locationError: string;
  initialLocation: Location | null;
}) => {
  const { t } = useTranslation();

  // Memoize the libraries array so it doesn't change on every render
  const libraries: Libraries = useMemo(() => ["places"], []);

  // Memoize the API key
  const googleMapsApiKey = useMemo(
    () => process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string, // Replace with your API key
    [process.env.REACT_APP_GOOGLE_MAPS_API_KEY]
  );

  const [selectedLocation, setSelectedLocation] = useState<Location>(
    initialLocation || {
      formattedAddress: "",
      lat: defaultCenter.lat,
      lng: defaultCenter.lng,
    }
  );

  const [inputValue, setInputValue] = useState(
    selectedLocation?.formattedAddress || ""
  );
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [error, setError] = useState<string | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const geocoder = useRef<google.maps.Geocoder | null>(null);

  // Load the Google Maps API (this should now only happen once)
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey,
    libraries,
  });

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry) {
        const lat = place.geometry.location?.lat() as number;
        const lng = place.geometry.location?.lng() as number;
        const formattedAddress = place.formatted_address || "";

        const location = { formattedAddress, lat, lng };
        setSelectedLocation(location);
        setInputValue(formattedAddress);
        onLocationChange(location);
      } else {
        onLocationChange(null, "Invalid location selected");
      }
    }
  };

  const handleMapClick = (event: google.maps.MapMouseEvent) => {
    if (event.latLng && geocoder.current) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();

      geocoder.current.geocode(
        { location: { lat, lng } },
        (results, status) => {
          if (status === "OK" && results && results[0]) {
            const formattedAddress = results[0].formatted_address;
            setSelectedLocation({ formattedAddress, lat, lng });
            setInputValue(formattedAddress);
            setError(null);
            onLocationChange({ formattedAddress, lat, lng });
          } else {
            setError(t("error.unknownLocation"));
            setSelectedLocation({
              formattedAddress: "Unknown location",
              lat,
              lng,
            });
            onLocationChange(null, t("error.unknownLocation"));
          }
        }
      );
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        rowGap: "14px",
        textAlign: "start",
        minHeight: "378px",
      }}
    >
      <Typography
        variant="body14SemiBold"
        sx={{
          color: colors.Dark600,
          fontWeight: 600,
        }}
      >
        {t("pages.events.createEvent.eventLocation")}
      </Typography>
      <Typography
        variant="body13Regular"
        sx={{
          color: colors.Dark600,
        }}
      >
        {t("pages.events.createEvent.searchForLocation")} <LocationIcon />{" "}
        {t("pages.events.createEvent.getYourLocation")}
      </Typography>

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={{ lat: selectedLocation.lat, lng: selectedLocation.lng }}
        zoom={15}
        onClick={handleMapClick}
        onLoad={(mapInstance: google.maps.Map) => {
          setMap(mapInstance);
          geocoder.current = new google.maps.Geocoder();
        }}
      >
        <Marker
          position={{ lat: selectedLocation.lat, lng: selectedLocation.lng }}
        />
      </GoogleMap>

      <Autocomplete
        onLoad={(autocomplete: google.maps.places.Autocomplete) =>
          (autocompleteRef.current = autocomplete)
        }
        onPlaceChanged={onPlaceChanged}
      >
        <TextField
          onKeyDown={handleKeyDown}
          placeholder={t("pages.events.createEvent.searchForArea")}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          error={!!locationError}
          helperText={locationError}
          InputProps={{
            startAdornment: !inputValue && (
              <InputAdornment position="start">
                <LocationInputPlaceholderIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <LocationInputEndIcon />
              </InputAdornment>
            ),
          }}
          fullWidth
        />
      </Autocomplete>
      {selectedLocation.formattedAddress ? (
        <Box
          sx={{ display: "flex", columnGap: "10px", alignItems: "flex-start" }}
        >
          <LocatorIcon />
          <Typography
            sx={{
              color: colors.Black,
              fontSize: "13px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "24px",
            }}
          >
            {selectedLocation.formattedAddress}
          </Typography>
        </Box>
      ) : (
        <></>
      )}
    </Box>
  );
};

export default MapWithPlacePicker;
